import React, { useState, useMemo, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import TemplateNode from "../nodes/TemplateNode";
import LeadsNode from "../nodes/LeadsNode";
import CampaignNode from "../nodes/campaign/CampaignNode";
import ExecuteButtonNode from "../nodes/ExecuteButtonNode";
import { Button } from "components/ui/button";

interface SidebarProps {
  templates: any[];
  leads: any[];
  campaigns: any[];
  identities: any[];
  editableNode?: any;
  nodesData: any[];
  onAddNodeToEditor: (nodeData: any) => void;
  onFetchTemplates: () => void;
  onFetchLeads: () => void;
  onFetchIdentities: () => void;
  onFetchCampaigns: () => void;
  onUpdateCampaignNodeData: (data: any) => void;
  onAddIdentity: (data: any) => void;
  onBackToDashboard: () => void;
  onSaveTemplate: () => void;
  sidebarLoading?: string | null;
}

const Sidebar: React.FC<SidebarProps> = ({
  templates,
  leads,
  campaigns,
  identities,
  editableNode,
  nodesData,
  onAddNodeToEditor,
  onFetchTemplates,
  onFetchLeads,
  onFetchIdentities,
  onFetchCampaigns,
  onUpdateCampaignNodeData,
  onAddIdentity,
  onBackToDashboard,
  onSaveTemplate,
  sidebarLoading = null,
}) => {
  const [isSidebarVisible, setSidebarVisible] = useState(true);
  const [currentNodeIndex, setCurrentNodeIndex] = useState<number | null>(null);
  const [accordionType, setAccordionType] = useState<string>("");

  const toggleSidebar = () => setSidebarVisible((v) => !v);

  const filterNodes = useCallback(
    (type: string) => nodesData.filter((n) => n.name === type),
    [nodesData]
  );

  const isAccordionVisible = useCallback(
    (index: number, type: string) =>
      currentNodeIndex === index && accordionType === type,
    [currentNodeIndex, accordionType]
  );

  const toggleAccordion = useCallback((index: number, type: string) => {
    if (currentNodeIndex === index && accordionType === type) {
      setCurrentNodeIndex(null);
      setAccordionType("");
    } else {
      setCurrentNodeIndex(index);
      setAccordionType(type);
    }
  }, [currentNodeIndex, accordionType]);

  const sidebarClasses = useMemo(
    () =>
      `relative transition-all duration-300 ease-in-out z-[5] shadow-md flex flex-col
       bg-surface dark:bg-dark-800
       border-r border-line dark:border-white/[0.07] ${isSidebarVisible
        ? "w-[300px] opacity-100"
        : "w-0 opacity-0 overflow-hidden"
      }`,
    [isSidebarVisible]
  );

  const toggleButtonStyle = useMemo(
    () => ({
      left: isSidebarVisible ? "285px" : "0px",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 6,
    }),
    [isSidebarVisible]
  );

  return (
    <>
      <div className={sidebarClasses} style={{ height: "calc(100dvh - 58px)" }}>

        <div className="sidebar-flow-header flex-shrink-0">
          <button
            onClick={onBackToDashboard}
            className="sidebar-back-btn"
            title="Back to Campaigns"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to Campaigns
          </button>
        </div>

        <div className="offcanvas-body px-3 py-2 flex-1 overflow-y-auto min-h-0">

          <p className="sidebar-steps-label">Workflow Steps</p>

          <div id="nodeAccordion" className="space-y-3">
            {filterNodes("Campaign").map((node, index) => (
              <div key={index} className={`sidebar-step-wrap sidebar-step-campaign${sidebarLoading === "campaigns" ? " sidebar-step-refreshing" : ""}`}>
                {sidebarLoading === "campaigns" && <div className="sidebar-refresh-bar" />}
                <CampaignNode
                  node={node}
                  campaigns={campaigns}
                  isOpen={isAccordionVisible(index, "campaign")}
                  isRefreshing={sidebarLoading === "campaigns"}
                  editableNode={editableNode}
                  onToggleAccordion={() => toggleAccordion(index, "campaign")}
                  onAddNode={(data) => onAddNodeToEditor(data)}
                  onUpdateCampaign={(data) => onUpdateCampaignNodeData(data)}
                  onRefreshCampaigns={onFetchCampaigns}
                />
              </div>
            ))}

            {filterNodes("Template").map((node, index) => (
              <div key={index} className={`sidebar-step-wrap sidebar-step-template${sidebarLoading === "templates" ? " sidebar-step-refreshing" : ""}`}>
                {sidebarLoading === "templates" && <div className="sidebar-refresh-bar" />}
                <TemplateNode
                  node={node}
                  templates={templates}
                  isOpen={isAccordionVisible(index, "template")}
                  isRefreshing={sidebarLoading === "templates"}
                  onToggleAccordion={() => toggleAccordion(index, "template")}
                  onAddNode={(data) => onAddNodeToEditor(data)}
                  onRefreshTemplates={onFetchTemplates}
                  onSaveTemplate={onSaveTemplate}
                />
              </div>
            ))}

            {filterNodes("Leads").map((node, index) => (
              <div key={index} className={`sidebar-step-wrap sidebar-step-leads${sidebarLoading === "leads" ? " sidebar-step-refreshing" : ""}`}>
                {sidebarLoading === "leads" && <div className="sidebar-refresh-bar" />}
                <LeadsNode
                  node={node}
                  leads={leads}
                  isOpen={isAccordionVisible(index, "leads")}
                  isRefreshing={sidebarLoading === "leads"}
                  onToggleAccordion={() => toggleAccordion(index, "leads")}
                  onAddNode={(data) => onAddNodeToEditor(data)}
                  onRefreshLeads={onFetchLeads}
                />
              </div>
            ))}

            {filterNodes("Select Identity").map((node, index) => (
              <div key={index} className={`sidebar-step-wrap sidebar-step-identity${sidebarLoading === "identities" ? " sidebar-step-refreshing" : ""}`}>
                {sidebarLoading === "identities" && <div className="sidebar-refresh-bar" />}
                <ExecuteButtonNode
                  node={node}
                  identities={identities}
                  isOpen={isAccordionVisible(index, "identity")}
                  isRefreshing={sidebarLoading === "identities"}
                  onToggleAccordion={() => toggleAccordion(index, "identity")}
                  onAddNode={(data) => onAddNodeToEditor(data)}
                  onAddIdentity={(data) => onAddIdentity(data)}
                  onRefreshIdentities={onFetchIdentities}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="sidebar-hint flex-shrink-0">
          <p className="sidebar-hint-title">💡 How it works</p>
          <p className="sidebar-hint-body">
            Add a <strong>Campaign</strong> node, connect it to a{" "}
            <strong>Template</strong> &amp; <strong>Leads</strong>, then link
            to an <strong>Identity</strong> to send.
          </p>
        </div>
      </div>

      <Button
        variant="primary"
        size="icon"
        className="toggle fixed w-[30px] !z-[30] h-[30px] transition-all duration-300"
        style={toggleButtonStyle}
        onClick={toggleSidebar}
      >
        {isSidebarVisible ? (
          <ChevronLeft className="w-4 h-4 text-white" />
        ) : (
          <ChevronRight className="w-4 h-4 text-white" />
        )}
      </Button>
    </>
  );
};

export default Sidebar;
